<script setup lang='ts'>
import { imageTransform } from '@qctsw/common'
import { logicComment } from '@/logic/common/comment'
import { LOAD_STATE } from '@/shared/types'
import { type LoginUserInfo, useAuth } from '@/stores/auth'
import { useCommentEvent } from '@/composables/useComment'
import type { CommentResponse, CommonType } from '@/server/types'
import noAvatar from '/noAvatar.jpg'

const props = withDefaults(defineProps<{
  dataType?: CommonType
  dataId?: number
}>(), {
  dataType: 'complaint',
})

const { showLoginPopup } = useLoginPopupEvent()
// 用户登录状态
const authStore = useAuth()
const { IS_LOGIN, USER_HEAD, USER_NAME, USER_INFO } = storeToRefs(authStore)

const { content, state: submitState, commentSubmit } = useComment()
async function commentHandler() {
  if (!props.dataId)
    return useMessage.warning('评论失败，请刷新页面重试')
  const result = await commentSubmit({ dataType: props.dataType, dataId: props.dataId })
  if (result) {
    content.value = ''
    useCommentEvent().addComment(result)
  }
}

const {
  data,
  state,
  pagination,
  getCommentList,
} = logicComment(props.dataType, props.dataId)

function eventHandler() {
  useCommentEvent().reset()
  useChildCommentEvent().reset()
  useCommentFavoriteEvent().reset()

  useCommentEvent().on((comment) => {
    const addComment: CommentResponse = {
      isGood: false,
      goodCount: 0,
      userId: (USER_INFO.value as LoginUserInfo).id,
      userName: USER_NAME.value,
      headImg: (USER_INFO.value as LoginUserInfo).headImg,
      replyLs: [],
      ...comment.data,
    }
    pagination.pagination.itemCount += 1
    data.value.unshift(addComment)
  })

  useChildCommentEvent().on((params) => {
    // 如果 firstLevelIndex === -1 说明是回复的是一级评论
    const index = params.firstLevelIndex === -1 ? params.secondLevelIndex : params.firstLevelIndex

    const addChildComment: CommentResponse['replyLs'][number] = {
      isGood: false,
      commentGoodCount: 0,
      userName: USER_NAME.value,
      headImg: (USER_INFO.value as LoginUserInfo).headImg,
      toUserName: params.toUserName,
      ...params.data,
    }
    data.value[index].replyLs.unshift(addChildComment)
  })

  useCommentFavoriteEvent().on((params) => {
    if (params.secondLevelIndex === -1) {
      const item = data.value[params.firstLevelIndex]
      item.isGood = params.isFavorite
      item.goodCount = item.goodCount + (params.isFavorite ? 1 : -1)
    }
    else {
      const item = data.value[params.firstLevelIndex].replyLs[params.secondLevelIndex]
      item.isGood = params.isFavorite
      item.commentGoodCount = item.commentGoodCount + (params.isFavorite ? 1 : -1)
    }
  })
}

useActivated({
  activatedFn: eventHandler,
  deactivatedFn: () => {
    useCommentEvent().reset()
    useChildCommentEvent().reset()
    useCommentFavoriteEvent().reset()
  },
})

getCommentList()
</script>

<template>
  <div v-if="props.dataId">
    <div class="font-bold text-5 mb-sm">
      评论
    </div>
    <div class="flex items-start">
      <NAvatar
        :size="45" round class="mr-sm flex-shrink-0"
        color="#2080f0"
        :src="imageTransform(USER_HEAD, { width: 100, height: 100 }) || noAvatar"
      />
      <div v-if="IS_LOGIN " flex-grow>
        <NInput
          v-model:value="content"
          class="bg-slate-1! rounded-md"
          type="textarea"
          rows="4"
          minlength="10"
          maxlength="500"
          show-count
          placeholder="说点什么..."
        />
        <div class="flex items-center justify-end mt-sm">
          <button
            class="px-7.5 btn-primary"
            :class="[content.trim().length < 1 || submitState === LOAD_STATE.LOADING ? 'cursor-not-allowed bg-blue-3' : 'cursor-pointer']"
            :loading="submitState === LOAD_STATE.LOADING"
            :disabled="content.trim().length < 1 || submitState === LOAD_STATE.LOADING"
            @click="commentHandler()"
          >
            <i v-if="submitState === LOAD_STATE.LOADING" class="i-eos-icons:loading mr-1 vertical-icon" />
            <span>评论</span>
          </button>
        </div>
      </div>
      <div v-else class="bg-slate-1 rounded-md text-center py-4xl flex-grow">
        看完啦，
        <button class="text-blue-5" @click="showLoginPopup('phone')">
          登录
        </button>
        分享一下感受吧～
      </div>
    </div>
    <div class="border-b-1 border-slate-2 my-sm" />
    <div class="font-bold text-5">
      <span>全部评论</span>
      <span v-if="pagination.pagination.itemCount" class="font-mono text-slate-5 ml-sm">{{ pagination.pagination.itemCount }}</span>
    </div>
    <LayoutCommentList
      v-if="props.dataId && props.dataId !== -1"
      :data-id="props.dataId"
      :data-type="props.dataType"
      :list="data"
      :total="pagination.pagination.itemCount"
      :is-loading="state === LOAD_STATE.LOADING"
      :has-more="state === LOAD_STATE.LOAD_MORE"
      @get-comment-list="getCommentList"
    />
  </div>
</template>

<style lang='scss' scoped>

</style>
